import { useEffect, useState } from "react";
import { X, Share, Download, MonitorSmartphone } from "lucide-react";

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isInstalled = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export default function InstallPwa() {
  const [deferred, setDeferred] = useState(() => window.__pwaPrompt || null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isInstalled()) return undefined;
    const sync = () => setDeferred(window.__pwaPrompt || null);
    const onPrompt = (e) => {
      e.preventDefault();
      window.__pwaPrompt = e;
      setDeferred(() => e);
    };
    const onOpen = () => setVisible(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("cetf:pwa-prompt", sync);
    window.addEventListener("cetf:install", onOpen);
    const dismissed = sessionStorage.getItem("pwa-install-dismissed") === "1";
    const onScroll = () => {
      if (!dismissed && window.scrollY > 250) {
        setVisible(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const t1 = setTimeout(() => { if (!dismissed) setVisible(true); }, 150000);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("cetf:pwa-prompt", sync);
      window.removeEventListener("cetf:install", onOpen);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(t1);
    };
  }, []);

  if (!visible || isInstalled()) return null;

  const close = () => {
    setVisible(false);
    sessionStorage.setItem("pwa-install-dismissed", "1");
  };

  const install = async () => {
    const p = deferred || window.__pwaPrompt;
    if (!p) return;
    p.prompt();
    await p.userChoice.catch(() => null);
    window.__pwaPrompt = null;
    setDeferred(null);
    close();
  };

  return (
    <div
      className="fixed bottom-5 left-4 sm:left-5 z-[60] w-[19.5rem] max-w-[calc(100vw-2.5rem)] bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-emerald-100 p-4 animate-fade-up"
      data-testid="install-pwa-banner"
      role="dialog"
      aria-label="Installer l'application CETF"
    >
      <button
        onClick={close}
        aria-label="Fermer la suggestion d'installation"
        data-testid="install-pwa-close"
        className="absolute top-2.5 right-2.5 text-slate-400 hover:text-slate-600 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <img src="/assets/pwa/icon-192.png" alt="" className="h-10 w-10 rounded-xl shadow-sm shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800 leading-snug">Installer l'appli CETF</p>
          {deferred ? (
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Accédez au site comme une appli, directement depuis votre écran d'accueil.
            </p>
          ) : isIos() ? (
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Sur iPhone : touchez <Share className="inline h-3.5 w-3.5 -mt-0.5 text-sky-600" aria-label="Partager" /> « Partager »
              puis « Sur l'écran d'accueil ».
            </p>
          ) : (
            <div className="text-xs text-slate-500 mt-1 leading-relaxed space-y-1.5" data-testid="install-pwa-instructions">
              <p className="flex items-start gap-1.5">
                <MonitorSmartphone className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-600" />
                <span><strong>Android / ordinateur</strong> : icône d'installation dans la barre d'adresse, ou menu ⋮ → « Installer l'application » / « Ajouter à l'écran d'accueil ».</span>
              </p>
              <p className="flex items-start gap-1.5">
                <Share className="h-3.5 w-3.5 mt-0.5 shrink-0 text-sky-600" />
                <span><strong>iPhone</strong> : <span aria-label="Partager">Partager</span> → « Sur l'écran d'accueil ».</span>
              </p>
            </div>
          )}
          {deferred ? (
            <button
              onClick={install}
              data-testid="install-pwa-confirm"
              className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-full px-3.5 py-1.5 transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Installer
            </button>
          ) : (
            <button
              onClick={close}
              data-testid="install-pwa-ok"
              className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-full px-3.5 py-1.5 transition-colors"
            >
              J'ai compris
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
